import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  TextField,
  Box,
  CircularProgress
} from '@mui/material';
import { countryCodeToName } from '../../../utils/countryUtils';
import wineRegionService from '../../../services/wineRegionService';

interface CountrySelectorProps {
  open: boolean;
  onClose: () => void;
  onSelectCountry: (countryCode: string, countryName: string) => void;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({ open, onClose, onSelectCountry }) => {
  const [existingCountries, setExistingCountries] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    const fetchExistingCountries = async () => {
      setLoading(true);
      try {
        const response = await wineRegionService.getCountries(1, 200);
        // Extract ISO codes from existing countries
        const existingCodes = response.data
          .filter(region => /^[A-Z]{2}$/.test(region.name))
          .map(region => region.name);
        setExistingCountries(existingCodes);
      } catch (error) {
        console.error('Failed to fetch existing countries:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchExistingCountries();
    }
  }, [open]);

  // Filter countries that are not already in the database
  const availableCountries = Object.entries(countryCodeToName)
    .filter(([code]) => !existingCountries.includes(code))
    .filter(([code, name]) => 
      name.toLowerCase().includes(searchTerm.toLowerCase()) || 
      code.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a[1].localeCompare(b[1])); // Sort by country name

  const handleSelectCountry = (code: string, name: string) => {
    onSelectCountry(code, name);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select a Country</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Search Countries"
          type="text"
          fullWidth
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
        />
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
            {availableCountries.length > 0 ? (
              availableCountries.map(([code, name]) => (
                <ListItem 
                  key={code} 
                  onClick={() => handleSelectCountry(code, name)}
                  divider
                  sx={{ cursor: 'pointer' }}
                >
                  <ListItemText primary={name} secondary={code} />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText primary="No countries available" secondary="All countries have been added" />
              </ListItem>
            )}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CountrySelector;
